import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { deleteUser, getUser, updateUser } from '../api/users';
import UserDetails from '../components/UserDetails';
import StatusMessage from '../components/StatusMessage';
import DeleteButton from '../components/DeleteButton';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import Card from '../components/common/Card';

const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userQuery = useQuery({
    queryKey: ['admin', 'user', id, token],
    queryFn: () => getUser(id!, token!),
    enabled: Boolean(id && token) && user?.user_role === 'admin',
  });

  const updateUserMutation = useMutation({
    mutationFn: (payload: { firstName: string; lastName: string }) =>
      updateUser(
        id!,
        { first_name: payload.firstName, last_name: payload.lastName },
        token!,
      ),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['admin', 'user', id, token], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: () => deleteUser(id!, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate('/admin/users');
    },
  });

  const handleSaveName = async (firstName: string, lastName: string) => {
    await updateUserMutation.mutateAsync({ firstName, lastName });
  };

  if (!user || user.user_role !== 'admin') {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <Card className="text-center">
            <h2 className="font-serif text-3xl text-[#2c201b]">Private profile.</h2>
            <p className="mt-3 text-[#5b5048]">You must be an admin to view user details.</p>
          </Card>
        </section>
      </div>
    );
  }

  if (userQuery.isLoading) {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="px-4 py-16 text-center">
          <StatusMessage type="loading" className="mx-auto max-w-sm" />
        </section>
      </div>
    );
  }

  if (userQuery.error && !userQuery.data) {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <StatusMessage
            type="error"
            message={userQuery.error instanceof Error ? userQuery.error.message : 'Failed to fetch user.'}
          />
        </section>
      </div>
    );
  }

  const userData = userQuery.data;
  if (!userData) {
    return null;
  }

  const errorMessage =
    (updateUserMutation.error instanceof Error && updateUserMutation.error.message) ||
    (deleteUserMutation.error instanceof Error && deleteUserMutation.error.message) ||
    '';

  return (
    <BookPage
      eyebrow="Admin"
      title="User details."
      intro="Review account metadata, update a name if needed, or remove access when necessary."
      bodyClassName="space-y-6"
    >
      <div className="flex justify-start sm:justify-end">
        <Button type="button" variant="secondary" onClick={() => navigate('/admin/users')}>
          Back to users
        </Button>
      </div>

      {errorMessage && <div className="rounded-[0.8rem] border border-red-200 bg-red-50/90 p-3 text-sm text-red-700">{errorMessage}</div>}

      <UserDetails user={userData} onSaveName={handleSaveName} showId />

      <div className="flex justify-end border-t border-[#8f7e69]/15 pt-6">
        <DeleteButton
          onDelete={() => deleteUserMutation.mutate()}
          loading={deleteUserMutation.isPending}
          confirmMessage="Are you sure you want to delete this user? This action cannot be undone."
        >
          Delete user
        </DeleteButton>
      </div>
    </BookPage>
  );
};

export default AdminUserDetailPage;
